/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { memo } from "react";
import { setErrorMsg, setOpenCustomDailog } from "../../../../store/helpers/helperReducers";
import { Sheet } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { RadixSelectControl } from "../../../ui/radix-select";
import { Checkbox } from "../../../ui/checkbox";
import { Label } from "../../../ui/label";
import { fileMgmtModuleList } from "../../../../globals/constants";
import { PasswordField } from "../../../Shared/Radix/Inputs/PasswordField";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { INodeFileMgmt } from "../../../../store/api/tasks/types";
import { useStoreDispatch } from "../../../../store/hooks";
import { IKeyValue } from "../../../Administrations/types";
import { nodeFileMgmtDefault } from "../../helpers";
import { isError, isIPv4, isIPv6, isServer, isValidHostName } from "../../../../utils/validations";


interface INodeFileMgmtProps {
    nodeFileMgmtFormData: INodeFileMgmt,
    setNodeFileMgmtFormData: React.Dispatch<React.SetStateAction<INodeFileMgmt>>,
    addToFileMgmtListHandler: React.MouseEventHandler<HTMLButtonElement> | undefined,
    errorMsg: IKeyValue;
    modelOpenMode: string;
}

const AddNodeFileMgmt: React.FC<INodeFileMgmtProps> = ({ errorMsg, nodeFileMgmtFormData, setNodeFileMgmtFormData, addToFileMgmtListHandler, modelOpenMode }) => {
    const dispatch = useStoreDispatch();

    const handleClose = () => {
        dispatch(setOpenCustomDailog(false));
        setNodeFileMgmtFormData(nodeFileMgmtDefault);
    };

    const onInputChange = (name: string, value: string) => {
        if ((["maxAttemptDuration", "maxAttempts", "randomUploadMaxDelay", "forwordingGroupIndex"].includes(name)
            && value.match(/[^0-9]/))
            || (name === "maxAttemptDuration" && value.length > 7)
            || (name === "maxAttempts" && value.length > 10)
            || (name === "randomUploadMaxDelay" && value.length > 2)
            || (name === "forwordingGroupIndex" && value.length > 10)
        ) {
            return;
        }

        setNodeFileMgmtFormData((oldValue) => ({ ...oldValue, [name]: value }));
        !!errorMsg?.[name] && dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    };

    const onSelectChange = (name: string, value: string) => {
        setNodeFileMgmtFormData((oldValue) => ({ ...oldValue, [name]: value }));
        !!errorMsg?.[name] && dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    };

    const onPasswordChange = (name: string, value: string) => {
        setNodeFileMgmtFormData((oldValue) => ({ ...oldValue, [name]: value }));
        !!errorMsg?.[name] && dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    };

    const checkboxHandler = (name: string, checked: boolean) => {
        if (name === "generateSCOSSSHKey" && !checked) {
            setNodeFileMgmtFormData((oldValue) => ({ ...oldValue, [name]: checked, autoPopulatedAuthorizedKeyFile: false }));
        } else {
            setNodeFileMgmtFormData((oldValue) => ({ ...oldValue, [name]: checked, password: "", autoPopulatedAuthorizedKeyFile: false }));
        }
        dispatch(setErrorMsg({ ...errorMsg, password: "" }));
    };

    const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        if (name === "password") {
            if (!nodeFileMgmtFormData.generateSCOSSSHKey && !!!value) {
                dispatch(setErrorMsg({ [name]: `Password is required!` }));
            }
        } else if (name === "hostName") {
            if (!!value) {
                const isValidHost = isValidHostName(value) || isServer(value) || isIPv4(value) || isIPv6(value);
                !isValidHost && dispatch(setErrorMsg({ [name]: `Host Name is invalid!` }));
            } else {
                dispatch(setErrorMsg({ [name]: `Host Name is required!` }));
            }
        } else {
            if (!!!value) {
                dispatch(setErrorMsg({ [name]: `This field is required!` }));
            }
        }
    };

    const moduleIdOptions = fileMgmtModuleList.map((item) => ({
        value: item.value,
        label: item.text,
    }));

    const overwriteModeOptions = [{ value: "Overwrite", label: "Overwrite" }];
    const priorityOptions = ["Primary", "Secondary", "Tertiary"].map((item) => ({ value: item, label: item }));
    const protocolOptions = ["FTP", "SCP", "TFTP"].map((item) => ({ value: item, label: item }));

    const footer = (
        <>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
                onClick={addToFileMgmtListHandler}
                disabled={isError(errorMsg)}
            >
                Save
            </Button>
        </>
    );

    return (
        <Sheet
            open={true}
            onClose={handleClose}
            title={`${modelOpenMode} File Management Parameter`}
            footer={footer}
            width="520px"
        >
            <form autoComplete="off">
                <div className="space-y-5 pt-2">
                    <RadixSelectControl
                        value={nodeFileMgmtFormData?.moduleId || ""}
                        onValueChange={(val) => onSelectChange("moduleId", val)}
                        options={moduleIdOptions}
                        placeholder="Module id"
                        autoFocus={true}
                    />

                    <p className="text-sm font-medium">Retry Parameters</p>

                    <InputField
                        label="Max Attempt Duration"
                        name="maxAttemptDuration"
                        value={nodeFileMgmtFormData?.maxAttemptDuration?.toString() || ""}
                        required
                        placeholder="Enter max attempt duration"
                        maxLength={7}
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />

                    <InputField
                        label="Max Attempts"
                        name="maxAttempts"
                        value={nodeFileMgmtFormData?.maxAttempts?.toString() || ""}
                        required
                        placeholder="Enter max attempts"
                        maxLength={10}
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />

                    <InputField
                        label="Random Upload Max Delay"
                        name="randomUploadMaxDelay"
                        value={nodeFileMgmtFormData?.randomUploadMaxDelay?.toString() || ""}
                        required
                        placeholder="Enter random upload max delay"
                        maxLength={2}
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />

                    <p className="text-sm font-medium">Upload Target Parameters</p>

                    <InputField
                        label="Host"
                        name="hostName"
                        value={nodeFileMgmtFormData?.hostName || ""}
                        required
                        placeholder="Enter host name"
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />

                    <InputField
                        label="User Name"
                        name="username"
                        value={nodeFileMgmtFormData?.username || ""}
                        required
                        placeholder="Enter user name"
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />

                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="generateSCOSSSHKey"
                            checked={nodeFileMgmtFormData?.generateSCOSSSHKey}
                            onCheckedChange={(checked) => checkboxHandler("generateSCOSSSHKey", !!checked)}
                        />
                        <Label htmlFor="generateSCOSSSHKey" className="cursor-pointer">Generate the SCOS SSH Key</Label>
                    </div>

                    {!nodeFileMgmtFormData?.generateSCOSSSHKey && (
                        <PasswordField
                            label="Password"
                            name="password"
                            value={nodeFileMgmtFormData?.password || ""}
                            placeholder="Enter password"
                            onChange={onPasswordChange}
                            onBlur={onBlurHandler}
                        />
                    )}

                    <InputField
                        label="Forwording Group Index"
                        name="forwordingGroupIndex"
                        value={nodeFileMgmtFormData?.forwordingGroupIndex?.toString() || ""}
                        required
                        placeholder="Enter forwording group index"
                        maxLength={10}
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />

                    <RadixSelectControl
                        value={nodeFileMgmtFormData?.overwriteMode || ""}
                        onValueChange={(val) => onSelectChange("overwriteMode", val)}
                        options={overwriteModeOptions}
                        placeholder="Overwrite Mode"
                        disabled={true}
                    />

                    <RadixSelectControl
                        value={nodeFileMgmtFormData?.priority || ""}
                        onValueChange={(val) => onSelectChange("priority", val)}
                        options={priorityOptions}
                        placeholder="Priority"
                    />

                    <RadixSelectControl
                        value={nodeFileMgmtFormData?.protocol || ""}
                        onValueChange={(val) => onSelectChange("protocol", val)}
                        options={protocolOptions}
                        placeholder="Protocol"
                    />

                    <InputField
                        label="Remote Path"
                        name="remotePath"
                        value={nodeFileMgmtFormData?.remotePath || ""}
                        required
                        placeholder="Enter remote path"
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />

                    <InputField
                        label="Remote Permission"
                        name="remotePermission"
                        value={nodeFileMgmtFormData?.remotePermission || ""}
                        required
                        placeholder="Enter remote permission"
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />
                </div>
            </form>
        </Sheet>
    );
};

export default memo(AddNodeFileMgmt);